import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { memo, useCallback } from 'react';
import { CommunicationLanguage } from '../types/kycFormTabs.types';

interface CommunicationLanguageSelectorProps {
  value: CommunicationLanguage | '';
  onValueChange: (value: CommunicationLanguage) => void;
  className?: string;
  error?: string;
}

/**
 * Component for selecting the communication language preference.
 * Required field for KYC form submission.
 * Optimized with memoization for performance.
 */
export const CommunicationLanguageSelector = memo(
  function CommunicationLanguageSelector({
    value,
    onValueChange,
    className,
    error,
  }: CommunicationLanguageSelectorProps) {
    const tForms = useFormsTranslation();

    // Memoize the value change handler to prevent unnecessary re-renders
    const handleValueChange = useCallback(
      (val: string) => {
        const lang = val as CommunicationLanguage;
        onValueChange(lang);
      },
      [onValueChange]
    );

    return (
      <div className={className}>
        <div className='space-y-2'>
          <Label className='text-sm font-medium'>
            {tForms.t('kyc.communicationLanguage.label')}
            <span className='text-destructive ml-1' aria-label='required field'>
              *
            </span>
          </Label>
          <p className='text-sm text-muted-foreground'>
            {tForms.t('kyc.communicationLanguage.infoText')}
          </p>
          <Select value={value} onValueChange={handleValueChange}>
            <SelectTrigger className='w-full sm:w-[300px]'>
              <SelectValue
                placeholder={tForms.t('kyc.communicationLanguage.label')}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='en'>
                {tForms.t('kyc.communicationLanguage.options.en')}
              </SelectItem>
              <SelectItem value='de'>
                {tForms.t('kyc.communicationLanguage.options.de')}
              </SelectItem>
              <SelectItem value='fr'>
                {tForms.t('kyc.communicationLanguage.options.fr')}
              </SelectItem>
              <SelectItem value='it'>
                {tForms.t('kyc.communicationLanguage.options.it')}
              </SelectItem>
            </SelectContent>
          </Select>
          {error && (
            <p className='text-sm text-destructive' role='alert'>
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }
);
